// src/components/HistoricoStatusTabela.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "../services/axiosInstance";
import styles from "../components/Modals/Modal.module.css";
import { SquarePen, Trash2, CircleCheckBig, SquareX } from "lucide-react";
import FasesTimeline from "./FasesTimeline";

const normalizar = (str = "") =>
  String(str).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/** ---- Permissões ----
 * Ver (podeVer): todos autenticados
 * Editar (podeEditar): admin, root, vereador presidente (não vice)
 * Excluir (podeExcluir): admin, root
 */
const eRootOuAdmin = (perfil) => {
  const tipo = normalizar(perfil?.tipo || "");
  return tipo === "root" || tipo === "admin";
};
const eVereadorPresidente = (perfil) => {
  const tipo = normalizar(perfil?.tipo || "");
  const cargo = normalizar(perfil?.cargo || "");
  if (tipo !== "vereador") return false;
  const ePresidente = cargo.includes("presidente") && !cargo.includes("vice");
  return ePresidente;
};

const safeUsuarioLabel = (h) => {
  const alias =
    h?.usuario?.alias ||
    h?.usuario?.nome ||
    h?.usuarioAlias ||
    h?.usuarioNome ||
    null;
  if (alias) return alias;
  if (h?.usuarioId != null) return `ID ${h.usuarioId}`;
  return "—";
};

const formatarNumeroSessao = (numeroRaw) => {
  if (!numeroRaw) return "";
  const num =
    typeof numeroRaw === "string" ? numeroRaw.split("/")[0] : String(numeroRaw);
  const nInt = parseInt(num, 10);
  if (Number.isNaN(nInt)) return String(num);
  return `${nInt}ª`;
};

const HistoricoStatusTabela = ({ pautaId, tipoMateriaId }) => {
  const [perfil, setPerfil] = useState(null);
  const [perfilLoaded, setPerfilLoaded] = useState(false);
  const [legislaturaAtual, setLegislaturaAtual] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  // edição
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    statusAnterior: "",
    statusNovo: "",
    motivo: "",
    sessaoId: "",
    usuarioId: "",
    dataCriacao: "", // ISO string YYYY-MM-DDTHH:mm
  });
  const [salvando, setSalvando] = useState(false);
  const [excluindoId, setExcluindoId] = useState(null);

  // bases auxiliares
  const [sessoes, setSessoes] = useState([]);

  const carregarPerfil = async () => {
    try {
      const endpoints = ["/usuarios/perfil", "/auth/perfil", "/perfil", "/usuarios/me"];
      for (const ep of endpoints) {
        try {
          const { data } = await axios.get(ep);
          setPerfil(data);
          break;
        } catch (_) { /* tenta próxima rota */ }
      }
    } finally {
      setPerfilLoaded(true);
    }
  };

  const carregarLegislatura = async () => {
    try {
      const { data } = await axios.get("/legislaturas/atual");
      setLegislaturaAtual(data || null);
    } catch {
      setLegislaturaAtual(null);
    }
  };

  const carregarSessoes = async () => {
    try {
      // lista enxuta de sessões só pra popular o select na edição
      const { data } = await axios.get("/sessoes?limit=200&order=desc");
      setSessoes(Array.isArray(data) ? data : []);
    } catch {
      setSessoes([]);
    }
  };

  const carregarHistorico = async () => {
    if (!pautaId) return;
    setCarregando(true);
    setErro(null);
    try {
      const res = await axios.get(
        `/historico-status?entidade=pauta&entidadeId=${pautaId}`
      );
      setHistorico(res.data || []);
    } catch (err) {
      setErro("Erro ao carregar histórico de status.");
      console.error(err);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarPerfil();
    carregarLegislatura();
    carregarSessoes();
  }, []);

  useEffect(() => {
    carregarHistorico();
  }, [pautaId]);

  // Regras de permissão
  const podeEditar = useMemo(
    () => eRootOuAdmin(perfil) || eVereadorPresidente(perfil),
    [perfil]
  );
  const podeExcluir = useMemo(() => eRootOuAdmin(perfil), [perfil]);

  if (!pautaId) return null;
  if (!perfilLoaded) return null; // evita flicker inicial
  if (carregando) return <p style={{ padding: 16 }}>Carregando histórico...</p>;
  if (erro) return <p style={{ padding: 16, color: "red" }}>{erro}</p>;
  // Mesmo sem histórico, vamos mostrar a timeline das fases abaixo.
  const collection = Array.isArray(historico) ? historico : [];

  const iniciarEdicao = (h) => {
    if (!podeEditar) return;
    setEditId(h.id);
    setForm({
      statusAnterior: h.statusAnterior ?? "",
      statusNovo: h.statusNovo ?? "",
      motivo: h.motivo ?? "",
      sessaoId: h.sessaoId ?? h.sessao?.id ?? "",
      usuarioId: h.usuarioId ?? h.usuario?.id ?? "",
      dataCriacao: h.dataCriacao ? new Date(h.dataCriacao).toISOString().slice(0,16) : "",
    });
  };

  const cancelarEdicao = () => {
    setEditId(null);
    setForm({
      statusAnterior: "",
      statusNovo: "",
      motivo: "",
      sessaoId: "",
      usuarioId: "",
      dataCriacao: "",
    });
  };

  const salvarEdicao = async (id) => {
    if (!podeEditar) return;
    setSalvando(true);
    try {
      await axios.patch(`/historico-status/${id}`, {
        statusAnterior: form.statusAnterior?.trim() || undefined,
        statusNovo: form.statusNovo?.trim() || undefined,
        motivo: form.motivo?.trim() || undefined,
        sessaoId: form.sessaoId ? Number(form.sessaoId) : undefined,
        usuarioId: form.usuarioId ? Number(form.usuarioId) : undefined,
        dataCriacao:
          form.dataCriacao
            ? new Date(form.dataCriacao).toISOString()
            : undefined,
      });
      setHistorico((prev) =>
        prev.map((x) =>
          x.id === id
            ? {
                ...x,
                statusAnterior: form.statusAnterior?.trim() || x.statusAnterior,
                statusNovo: form.statusNovo?.trim() || x.statusNovo,
                motivo: form.motivo?.trim() ?? x.motivo,
                sessaoId: form.sessaoId ? Number(form.sessaoId) : x.sessaoId,
                usuarioId: form.usuarioId ? Number(form.usuarioId) : x.usuarioId,
                // otimismo: se mudar sessao/usuario, pelo menos mantenha id/alias se existia
                sessao:
                  form.sessaoId
                    ? (prev.find(p=>p.id===id)?.sessao && Number(prev.find(p=>p.id===id)?.sessao?.id)===Number(form.sessaoId)
                        ? prev.find(p=>p.id===id)?.sessao
                        : { id: Number(form.sessaoId) })
                    : x.sessao,
                usuario:
                  form.usuarioId
                    ? (prev.find(p=>p.id===id)?.usuario && Number(prev.find(p=>p.id===id)?.usuario?.id)===Number(form.usuarioId)
                        ? prev.find(p=>p.id===id)?.usuario
                        : { id: Number(form.usuarioId) })
                    : x.usuario,
                dataCriacao:
                  form.dataCriacao
                    ? new Date(form.dataCriacao).toISOString()
                    : x.dataCriacao,
              }
            : x
        )
      );
      cancelarEdicao();
    } catch (err) {
      console.error(err);
      alert("Falha ao salvar alterações.");
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (id) => {
    if (!podeExcluir) return;
    if (!window.confirm("Excluir este registro do histórico?")) return;
    setExcluindoId(id);
    try {
      await axios.delete(`/historico-status/${id}`);
      setHistorico((prev) => prev.filter((x) => x.id !== id));
    } catch (err) {
      console.error(err);
      alert("Falha ao excluir registro.");
    } finally {
      setExcluindoId(null);
    }
  };

  const mostrarColunaAcoes = podeEditar || podeExcluir;

  const legislaturaRotulo = legislaturaAtual?.numero
    ? `${legislaturaAtual.numero}ª Legislatura`
    : "";

  return (
    <div className={styles.pautaGrupo} style={{background: "transparent"}}>
      {/* Timeline das fases (passadas, atual, próximas) com prazos */}
      <div>
        <FasesTimeline
          pautaId={pautaId}
          tipoMateriaId={tipoMateriaId}
          historico={collection}
        />
      </div>
    </div>
  );
};

export default HistoricoStatusTabela;
